import { useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { LOGO_DISPLAY } from '../../lib/seo';
import { Menu, type CategoryItem } from './Menu';
import { SocialIconList } from './SocialIconList';

export type TrendingItem = {
  id: string;
  slug: string;
  title: string;
};

function TrendingTicker({ items }: { items: TrendingItem[] }) {
  const swiperRef = useRef<SwiperType | null>(null);
  if (items.length === 0) return null;
  return (
    <div className="topbar-trending">
      <span>آخر خبر</span>
      <div className="trending-slider">
        <Swiper
          modules={[Autoplay, Navigation]}
          dir="rtl"
          direction="horizontal"
          slidesPerView={1}
          loop={items.length > 1}
          allowTouchMove={false}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          speed={700}
          onSwiper={(s) => (swiperRef.current = s)}
        >
          {items.map((item) => (
            <SwiperSlide key={item.id}>
              <div className="trending-item">
                <p>
                  <Link to="/articles/$slug" params={{ slug: item.slug }}>
                    {item.title}
                  </Link>
                </p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <button
          type="button"
          className="trending-nav prev"
          aria-label="السابق"
          onClick={() => swiperRef.current?.slidePrev()}
        >
          <i className="fas fa-chevron-left" />
        </button>
        <button
          type="button"
          className="trending-nav next"
          aria-label="التالي"
          onClick={() => swiperRef.current?.slideNext()}
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>
    </div>
  );
}

function TopbarDate() {
  const now = new Date();
  const formatted = new Intl.DateTimeFormat('ar-MA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(now);
  return <p>{formatted}</p>;
}

export function Header({
  categories = [],
  trending = [],
}: {
  categories?: CategoryItem[];
  trending?: TrendingItem[];
}) {
  return (
    <header className="header-area">
      <div className="header-topbar">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <TrendingTicker items={trending} />
            </div>
            <div className="col-lg-4">
              <div className="topbar-social d-flex align-items-center">
                <TopbarDate />
                <SocialIconList className="social" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="header-centerbar">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-4">
              <div className="logo">
                <Link to="/">
                  {/* Intrinsic size is required: the logo is the first eager
                      image on every page, so without it the header reflows once
                      the file lands and takes the whole document with it. */}
                  <img
                    src={LOGO_DISPLAY.url}
                    alt="تلي صافي"
                    width={LOGO_DISPLAY.width}
                    height={LOGO_DISPLAY.height}
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="header-menubar">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12">
              <div className="header-menu">
                <Menu categories={categories} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
